import {
  Directive,
  ElementRef,
  inject,
  OnDestroy,
  output,
  AfterViewInit,
  input,
  effect,
} from '@angular/core';
import { createTooltip } from './tooltip';

@Directive({
  selector: '[lib-truncate-tooltip]',
  standalone: true,
  exportAs: 'libTruncateTooltip',
})
export class TruncateTooltipDirective implements AfterViewInit, OnDestroy {
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private tooltip: HTMLElement | null = null;
  public readonly disabled = input<boolean>(false);

  public removedTooltip = output<void>();
  private intersectionObserver: IntersectionObserver | null = null;

  private readonly obseveToCreateTooltip = effect(() => {
    if (!this.disabled()) {
      this.addTooltip();
    } else {
      this.removeTooltip();
      this.removeListeners();
    }
  })

  public ngAfterViewInit(): void {
    this.addTooltip();
  }

  public addTooltip(): void {
    this.removeListeners();
    requestAnimationFrame(() => {
      if (this.elementRef?.nativeElement?.textContent && !this.disabled()) {
        this.observeVibility();
        this.elementRef.nativeElement.classList.add('truncate');
        this.elementRef.nativeElement.addEventListener('mouseenter', this.createTooltip, { passive: true });
        this.elementRef.nativeElement.addEventListener('mouseleave', this.removeTooltip, { passive: true });
        this.elementRef.nativeElement.addEventListener('wheel', this.removeTooltip, { passive: true });
      }
    })
  }

  public ngOnDestroy(): void {
    this.removeTooltip();
    this.removeListeners();
    this.intersectionObserver?.disconnect();
  }

  private removeListeners(): void {
    this.elementRef.nativeElement.removeEventListener('mouseenter', this.createTooltip);
    this.elementRef.nativeElement.removeEventListener('mouseleave', this.removeTooltip);
    this.elementRef.nativeElement.removeEventListener('wheel', this.removeTooltip);
  }

  public readonly removeTooltip = (): void => {
    if (this.tooltip) {
      this.tooltip.remove();
      this.tooltip = null;
      this.removedTooltip.emit();
    }
  };

  private readonly createTooltip = (): void => {
    const isTruncated = this.elementRef.nativeElement.offsetWidth < this.elementRef.nativeElement.scrollWidth;
    if (!isTruncated) return;
    this.tooltip = createTooltip({
      content: this.elementRef.nativeElement.textContent,
      title: null,
      element: this.elementRef.nativeElement,
      hasDistance: false,
      xAxis: 'end',
      yAxis: 'end',
    });
  };

  private observeVibility(): void {
    this.intersectionObserver = new IntersectionObserver(entries => {
      if (!entries[0].isIntersecting) this.removeTooltip();
    });

    this.intersectionObserver.observe(this.elementRef.nativeElement);
  }
}